import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAlarmLogQuery, useAcknowledgeAlarmLogMutation } from '../services/alarmApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useGetUsersQuery } from '../services/userApi';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { API_CONFIG } from '../config/api.config';
import { CheckSquare } from 'lucide-react';
import { AlarmAcknowledgeDialog } from '../components/alarms/AlarmAcknowledgeDialog';
import { formatSensorSerial } from '../utils/sensorUtils';
import { normalizeAlarmType, alarmTypeShortLabel } from '../utils/alarmDisplay';
import { Dialog } from '../components/common/Dialog';
import { filterAlarmLogsBySensorAccess } from '../utils/alarmAccess';

function getWorstValue(logs: any[], alarmType: string) {
  const k = normalizeAlarmType(alarmType);
  if (k === 'high') {
    return Math.max(...logs.map(l => Number(l.alarm_values)));
  }
  if (k === 'low') {
    return Math.min(...logs.map(l => Number(l.alarm_values)));
  }
  return logs[0]?.alarm_values ?? '-';
}

function formatDuration(ms: number) {
  if (isNaN(ms) || ms < 0) return '-';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'unack', label: 'Unacknowledged First' },
];

export default function AlarmLogPage() {
  const navigate = useNavigate();
  const { data: alarmLogRaw, isLoading, isError, error, refetch } = useGetAlarmLogQuery({ includeAcknowledged: true });
  const { data: sensors } = useGetSensorListQuery();
  const alarmLog = useMemo(
    () => filterAlarmLogsBySensorAccess(alarmLogRaw, sensors),
    [alarmLogRaw, sensors],
  );
  const { data: users = [] } = useGetUsersQuery();
  const [sortBy, setSortBy] = useState('newest');
  const user = useSelector((state: RootState) => state.auth.user);
  const [acknowledgeAlarmLog] = useAcknowledgeAlarmLogMutation();
  const [ackModalLog, setAckModalLog] = useState<any | null>(null);
  const [ackModalNote, setAckModalNote] = useState('');
  const [ackModalSubmitting, setAckModalSubmitting] = useState(false);
  const [ackModalError, setAckModalError] = useState<string | null>(null);
  const [editNoteLogId, setEditNoteLogId] = useState<number | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [acknowledgmentMap, setAcknowledgmentMap] = useState<Record<string, string>>({});
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [isBulkAcknowledging, setIsBulkAcknowledging] = useState(false);
  const [bulkAckModalOpen, setBulkAckModalOpen] = useState(false);
  const [bulkAckNote, setBulkAckNote] = useState('');
  const [bulkAckError, setBulkAckError] = useState<string | null>(null);

  // Use email → firstName → id as the display name for acknowledged_by / notes
  const userDisplayName = user?.email || user?.firstName || (user?.id ? String(user.id) : null);

  const getSensorInfo = (sensorId: number) => {
    if (!sensors) return { name: '-', serialNo: '-' };
    const sensor = sensors.find((s: any) => s.id === sensorId);
    return sensor ? { name: sensor.name, serialNo: sensor.serialNo } : { name: '-', serialNo: '-' };
  };

  const getUserName = (userId: any): string => {
    if (!userId) return '-';
    // Non-numeric strings (e.g. 'System', email addresses) are already display names
    if (typeof userId === 'string' && isNaN(Number(userId))) return userId;
    const u = (users as any[]).find(u => u.id === userId || u.id === Number(userId));
    if (!u) return String(userId);
    return u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || String(userId);
  };

  const getUserEmail = (userId: any): string => {
    if (!userId) return '-';
    // Already an email string
    if (typeof userId === 'string' && isNaN(Number(userId))) return userId;
    const u = (users as any[]).find(u => u.id === userId || u.id === Number(userId));
    return u?.email || getUserName(userId);
  };

  // Fetch audit logs to get acknowledged_by information
  useEffect(() => {
    const fetchAcknowledgmentData = async () => {
      if (!user) return;
      
      try {
        // Fetch audit logs filtered by alarm acknowledgment action
        const ackUrl = new URL(`${API_CONFIG.baseURL}/audit-logs`);
        ackUrl.searchParams.set('action', AUDIT_ACTIONS.ALARM_ACKNOWLEDGE);
        ackUrl.searchParams.set('limit', '1000');
        const response = await fetch(ackUrl.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-company-id': user.companyID?.toString() || '',
              'x-company-name': user.companyName || '',
            },
          }
        );

        if (!response.ok) {
          console.error('Failed to fetch audit logs for acknowledgments');
          return;
        }

        const auditLogs = await response.json();
        
        // Parse audit log descriptions to extract Event ID and acknowledged_by
        // Format: "Alarm acknowledged - Sensor ID: 42, Event ID: a07366bc-e8dc-46d3-b71b-9ec4865f9205, Acknowledged by: adam"
        const map: Record<string, string> = {};
        
        // Sort audit logs by timestamp (newest first) to get most recent acknowledgments
        const sortedAuditLogs = [...auditLogs].sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        
        sortedAuditLogs.forEach((log: any) => {
          if (log.description && log.description.includes('Event ID:')) {
            // Extract Event ID and acknowledged_by from description
            // Format examples:
            // "Alarm acknowledged - Sensor ID: 42, Event ID: a07366bc-e8dc-46d3-b71b-9ec4865f9205, Acknowledged by: adam"
            // "Alarm acknowledged from Alarm Log - Event ID: a07366bc-e8dc-46d3-b71b-9ec4865f9205, Sensor ID: 42, Sensor Name: Sensor1, Alarm Type: high, Acknowledged by: adam"
            // "Bulk alarm acknowledgment - Acknowledged 5 active alarms for 10 sensors during maintenance/calibration. Sensors: Sensor1, Sensor2, Acknowledged by: adam"
            const eventIdMatch = log.description.match(/Event ID:\s*([a-f0-9-]+)/i);
            const acknowledgedByMatch = log.description.match(/Acknowledged by:\s*([^\s,\.]+)/i);
            
            if (eventIdMatch && acknowledgedByMatch) {
              const eventId = eventIdMatch[1].trim();
              const acknowledgedBy = acknowledgedByMatch[1].trim();
              // Use the first (most recent) acknowledgment for each event ID
              if (!map[eventId] && acknowledgedBy) {
                map[eventId] = acknowledgedBy;
              }
            }
          }
        });
        
        setAcknowledgmentMap(map);
      } catch (error) {
        console.error('Error fetching acknowledgment data from audit logs:', error);
      }
    };

    if (user && alarmLog && Array.isArray(alarmLog) && alarmLog.length > 0) {
      fetchAcknowledgmentData();
    }
  }, [user, alarmLog]);

  // Group by event_id to show one entry per alarm event.
  const events = useMemo(() => {
    if (!Array.isArray(alarmLog)) {
      return [];
    }
    const eventMap = new Map();

    // Group all logs by event_id
    for (const log of alarmLog) {
      const eventId = log.event_id || `log-${log.idAlarmLog}`;
      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, []);
      }
      eventMap.get(eventId).push(log);
    }

    // Create a summary for each event
    return Array.from(eventMap.values()).map((eventLogs: any[]) => {
      // Sort logs by time to reliably find the first one
      eventLogs.sort((a, b) => new Date(a.activated).getTime() - new Date(b.activated).getTime());
      
      const initialLog = eventLogs[0];
      const resetLog = eventLogs.find(l => l.alarm_reset);
      
      const isAcknowledged = eventLogs.every(l => l.acknowledged);
      // Find the last person who acknowledged any part of the event from alarm log
      const lastAcknowledgedLog = [...eventLogs].reverse().find(l => l.acknowledged_by);
      
      // Try to get acknowledged_by from audit logs if not found in alarm log
      const eventId = initialLog.event_id;
      const auditLogAcknowledgedBy = eventId ? acknowledgmentMap[eventId] : null;

      return {
        ...initialLog,
        // Ensure key properties are from the initial log
        idAlarmLog: initialLog.idAlarmLog,
        event_id: initialLog.event_id,
        // Summarize properties
        alarm_reset: resetLog ? resetLog.alarm_reset : null,
        alarm_values: getWorstValue(eventLogs, initialLog.alarm_type),
        acknowledged: isAcknowledged,
        // Use acknowledged_by from alarm log if available, otherwise try audit log
        acknowledged_by: lastAcknowledgedLog?.acknowledged_by || auditLogAcknowledgedBy || null,
        notes: initialLog.notes, // Or maybe summarize notes? For now, first is fine.
      };
    });
  }, [alarmLog, acknowledgmentMap]);

  // Sorting logic
  let sortedEvents = [...events];
  if (sortBy === 'newest') {
    sortedEvents.sort((a, b) => (b.activated ? new Date(b.activated).getTime() : 0) - (a.activated ? new Date(a.activated).getTime() : 0));
  } else if (sortBy === 'oldest') {
    sortedEvents.sort((a, b) => (a.activated ? new Date(a.activated).getTime() : 0) - (b.activated ? new Date(b.activated).getTime() : 0));
  } else if (sortBy === 'unack') {
    sortedEvents.sort((a, b) => {
      if (a.acknowledged_by === b.acknowledged_by) {
        return (b.activated ? new Date(b.activated).getTime() : 0) - (a.activated ? new Date(a.activated).getTime() : 0);
      }
      return a.acknowledged_by ? 1 : -1;
    });
  }

  const openAckModal = (log: any) => {
    if (!userDisplayName || log.acknowledged) return;
    setAckModalNote(typeof log.notes === 'string' ? log.notes : '');
    setAckModalError(null);
    setAckModalLog(log);
  };

  const closeAckModal = () => {
    if (ackModalSubmitting) return;
    setAckModalLog(null);
    setAckModalNote('');
    setAckModalError(null);
  };

  const dismissBatteryAlarm = async (log: any) => {
    if (!userDisplayName) return;
    const allLogs = Array.isArray(alarmLog) ? alarmLog : [];
    const logsToAck = allLogs.filter((l: any) => l.event_id === log.event_id && !l.acknowledged);
    if (logsToAck.length === 0) return;
    try {
      await Promise.all(
        logsToAck.map((l: any) =>
          acknowledgeAlarmLog({
            logId: l.idAlarmLog,
            body: {
              acknowledged: true,
              acknowledged_by: userDisplayName,
              alarm_reset: new Date().toISOString(),
              notes: `Battery low (dismissed) —${userDisplayName}`,
            },
          })
        )
      );
      refetch();
    } catch (e) {
      console.error('Battery dismiss error:', e);
    }
  };

  const confirmAckFromModal = async () => {
    if (!userDisplayName || !ackModalLog?.event_id) return;

    const trimmedNote = ackModalNote.trim();
    if (!trimmedNote) {
      setAckModalError('Please enter a note before acknowledging.');
      return;
    }

    const log = ackModalLog;
    const allLogs = Array.isArray(alarmLog) ? alarmLog : [];
    const logsToAcknowledge = allLogs.filter(
      (l: any) => l.event_id === log.event_id && !l.acknowledged
    );

    if (logsToAcknowledge.length === 0) {
      setAckModalError('This alarm may have already been acknowledged. Refresh and try again.');
      return;
    }

    const noteWithUser = `${trimmedNote} —${userDisplayName}`;

    setAckModalSubmitting(true);
    setAckModalError(null);
    try {
      await Promise.all(
        logsToAcknowledge.map((l: any) =>
          acknowledgeAlarmLog({
            logId: l.idAlarmLog,
            body: {
              acknowledged: true,
              acknowledged_by: userDisplayName,
              alarm_reset: new Date().toISOString(),
              notes: noteWithUser,
            },
          })
        )
      );

      const sensorInfo = getSensorInfo(log.sensor_id);
      await logAuditEvent(
        AUDIT_ACTIONS.ALARM_ACKNOWLEDGE,
        `Alarm acknowledged from Alarm Log - Event ID: ${log.event_id}, Sensor ID: ${log.sensor_id}, Sensor Name: ${sensorInfo.name}, Alarm Type: ${log.alarm_type}, Acknowledged by: ${userDisplayName}`,
        user
      );

      setAckModalLog(null);
      setAckModalNote('');
      refetch();
    } catch (e) {
      console.error('Acknowledge error:', e);
      setAckModalError('Could not acknowledge alarm. Please try again.');
    } finally {
      setAckModalSubmitting(false);
    }
  };

  const ackModalSerialDisplay =
    ackModalLog && sensors
      ? (() => {
          const s = sensors.find((x: any) => x.id === ackModalLog.sensor_id);
          return s
            ? formatSensorSerial(s.serialNo, s.type)
            : String(ackModalLog.sensor_id ?? '—');
        })()
      : '';

  const unacknowledgedEvents = sortedEvents.filter(e => !e.acknowledged);
  const allSelected = unacknowledgedEvents.length > 0 && unacknowledgedEvents.every(e => selectedEventIds.has(e.event_id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedEventIds(new Set());
    } else {
      setSelectedEventIds(new Set(unacknowledgedEvents.map(e => e.event_id)));
    }
  };

  const toggleSelectEvent = (eventId: string) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const openBulkAckModal = () => {
    if (!userDisplayName || selectedEventIds.size === 0) return;
    setBulkAckNote('');
    setBulkAckError(null);
    setBulkAckModalOpen(true);
  };

  const closeBulkAckModal = () => {
    if (isBulkAcknowledging) return;
    setBulkAckModalOpen(false);
    setBulkAckNote('');
    setBulkAckError(null);
  };

  const confirmBulkAcknowledge = async () => {
    if (!userDisplayName || selectedEventIds.size === 0) return;
    const trimmed = bulkAckNote.trim();
    if (!trimmed) {
      setBulkAckError('Please enter a note before acknowledging.');
      return;
    }

    const noteWithUser = `${trimmed} —${userDisplayName}`;
    const allLogs = Array.isArray(alarmLog) ? alarmLog : [];

    setIsBulkAcknowledging(true);
    setBulkAckError(null);
    try {
      for (const eventId of selectedEventIds) {
        const logsToAck = allLogs.filter(
          (l: any) => l.event_id === eventId && !l.acknowledged
        );
        await Promise.all(
          logsToAck.map((l: any) =>
            acknowledgeAlarmLog({
              logId: l.idAlarmLog,
              body: {
                acknowledged: true,
                acknowledged_by: userDisplayName,
                alarm_reset: new Date().toISOString(),
                notes: noteWithUser,
              },
            })
          )
        );
      }

      const sensorNames = sortedEvents
        .filter(e => selectedEventIds.has(e.event_id))
        .map(e => getSensorInfo(e.sensor_id).name)
        .join(', ');
      await logAuditEvent(
        AUDIT_ACTIONS.ALARM_ACKNOWLEDGE,
        `Bulk alarm acknowledgment - Acknowledged ${selectedEventIds.size} alarm event(s). Sensors: ${sensorNames}, Acknowledged by: ${userDisplayName}`,
        user
      );
      setSelectedEventIds(new Set());
      setBulkAckModalOpen(false);
      setBulkAckNote('');
      refetch();
    } catch (e) {
      console.error('Bulk acknowledge error:', e);
      setBulkAckError('Could not acknowledge all selected alarms. Please try again.');
    } finally {
      setIsBulkAcknowledging(false);
    }
  };

  const handleEditNote = (logId: number, currentNote: string) => {
    setEditNoteLogId(logId);
    setNoteInput(currentNote || '');
  };

  const handleSaveNote = async (logId: number) => {
    if (!userDisplayName) return;
    setIsSavingNote(true);
    try {
      // Append username to note
      const noteWithUser = noteInput.trim() ? `${noteInput.trim()} —${userDisplayName}` : '';
      await acknowledgeAlarmLog({ logId, body: { notes: noteWithUser } }).unwrap();
      setEditNoteLogId(null);
      setNoteInput('');
      refetch();
    } catch (e) {
      console.error('Save note error:', e);
    } finally {
      setIsSavingNote(false);
    }
  };

  const fmtTime = (raw: any) => {
    if (!raw) return '-';
    const d = new Date(raw);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const typeBadge = (type: string) => {
    const k = normalizeAlarmType(type);
    const cls =
      k === 'high'
        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        : k === 'low'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        : k === 'battery'
        ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
        : k === 'missing_data'
        ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300'
        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
        {k === 'high' ? '▲ High' : k === 'low' ? '▼ Low' : alarmTypeShortLabel(type)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8">

        {/* Header */}
        <div className="mb-4 md:mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Alarm Log</h1>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{sortedEvents.length} event{sortedEvents.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedEventIds.size > 0 && (
              <button
                onClick={openBulkAckModal}
                disabled={isBulkAcknowledging || !userDisplayName}
                title={!userDisplayName ? 'Sign in to acknowledge' : undefined}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              >
                <CheckSquare className="h-4 w-4 mr-1.5" />
                {isBulkAcknowledging ? 'Acknowledging...' : `Ack. Selected (${selectedEventIds.size})`}
              </button>
            )}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3" />
            <span className="text-gray-600 dark:text-gray-300">Loading...</span>
          </div>
        )}

        {isError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700 dark:text-red-400">
              ⚠️ Error loading alarm log: {(error as any)?.message || 'Unknown error'}
            </p>
          </div>
        )}

        {/* ── Mobile: card list (below md) ── */}
        {sortedEvents.length > 0 && (
          <div className="md:hidden space-y-2">
            {sortedEvents.map((log, idx) => {
              const sensor = getSensorInfo(log.sensor_id);
              const isUnack = !log.acknowledged;
              return (
                <div
                  key={log.idAlarmLog || idx}
                  className={`rounded-lg border px-4 py-3 bg-app-panel dark:bg-gray-800 ${
                    isUnack
                      ? 'border-red-300 dark:border-red-700'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {typeBadge(log.alarm_type)}
                        <span
                          className="text-sm font-semibold text-gray-900 dark:text-white truncate cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline"
                          title="View sensor graph"
                          onClick={() => navigate(`/sensors/${log.sensor_id}?from=alarm-log`)}
                        >{sensor.name}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                        <div><span className="font-medium text-gray-700 dark:text-gray-300">{log.alarm_values ?? '-'}°C</span> · {fmtTime(log.activated)}</div>
                        {log.alarm_reset && <div>Reset: {fmtTime(log.alarm_reset)} · {log.activated && log.alarm_reset ? formatDuration(new Date(log.alarm_reset).getTime() - new Date(log.activated).getTime()) : ''}</div>}
                        {log.acknowledged_by && <div>Ack: {getUserName(log.acknowledged_by)}</div>}
                        {log.notes && <div className="italic truncate">{log.notes}</div>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      {isUnack ? (
                        normalizeAlarmType(log.alarm_type) === 'battery' ? (
                          <button
                            className="px-2.5 py-1 text-xs font-medium rounded text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-60"
                            onClick={() => dismissBatteryAlarm(log)}
                            disabled={!userDisplayName}
                            title={!userDisplayName ? 'Sign in to dismiss' : 'Dismiss battery alert'}
                          >
                            Dismiss
                          </button>
                        ) : (
                          <button
                            className="px-2.5 py-1 text-xs font-medium rounded text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                            onClick={() => openAckModal(log)}
                            disabled={!userDisplayName}
                            title={!userDisplayName ? 'Sign in to acknowledge' : 'Review and acknowledge'}
                          >
                            Ack
                          </button>
                        )
                      ) : (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">✓ Ack'd</span>
                      )}
                      <button
                        className="text-xs text-indigo-500 dark:text-indigo-400 underline"
                        onClick={() => handleEditNote(log.idAlarmLog, log.notes)}
                      >Note</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Desktop: compact table (md+) ── */}
        {sortedEvents.length > 0 && (
          <div className="hidden md:block bg-app-panel dark:bg-gray-800 shadow rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2.5 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                        disabled={unacknowledgedEvents.length === 0}
                        className="w-4 h-4 text-indigo-600 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        title="Select all unacknowledged" />
                    </th>
                    <th className="px-3 py-2.5 text-left">Sensor</th>
                    <th className="px-3 py-2.5 text-left">Type</th>
                    <th className="px-3 py-2.5 text-left">Value</th>
                    <th className="px-3 py-2.5 text-left">Start</th>
                    <th className="px-3 py-2.5 text-left">End</th>
                    <th className="px-3 py-2.5 text-left">Duration</th>
                    <th className="px-3 py-2.5 text-left">Acknowledged By</th>
                    <th className="px-3 py-2.5 text-left">Notes</th>
                    <th className="px-3 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedEvents.map((log, idx) => {
                    const sensor = getSensorInfo(log.sensor_id);
                    return (
                      <tr key={log.idAlarmLog || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-3 py-2.5 w-8">
                          {!log.acknowledged && normalizeAlarmType(log.alarm_type) !== 'battery' && (
                            <input type="checkbox" checked={selectedEventIds.has(log.event_id)}
                              onChange={() => toggleSelectEvent(log.event_id)}
                              className="w-4 h-4 text-indigo-600 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700" />
                          )}
                        </td>
                        <td className="px-3 py-2.5 max-w-[160px]">
                          <div
                            className="truncate font-medium text-gray-900 dark:text-white cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline"
                            title={`View graph for ${sensor.name}`}
                            onClick={() => navigate(`/sensors/${log.sensor_id}?from=alarm-log`)}
                          >{sensor.name}</div>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{typeBadge(log.alarm_type)}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap font-medium text-gray-900 dark:text-white">{log.alarm_values ?? '-'}°C</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-300">{fmtTime(log.activated)}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-300">{fmtTime(log.alarm_reset)}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-300">
                          {log.activated && log.alarm_reset ? formatDuration(new Date(log.alarm_reset).getTime() - new Date(log.activated).getTime()) : '-'}
                        </td>
                        <td className="px-3 py-2.5 max-w-[100px]">
                          <div className="truncate text-gray-600 dark:text-gray-300" title={getUserEmail(log.acknowledged_by)}>{getUserName(log.acknowledged_by)}</div>
                        </td>
                        <td className="px-3 py-2.5 max-w-[140px]">
                          <div className="flex items-center gap-1">
                            <span className="truncate text-gray-600 dark:text-gray-300 text-xs" title={log.notes || ''}>{log.notes || <span className="text-gray-400">—</span>}</span>
                            <button className="text-indigo-500 dark:text-indigo-400 text-xs underline flex-shrink-0" onClick={() => handleEditNote(log.idAlarmLog, log.notes)}>Edit</button>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-right">
                          {!log.acknowledged ? (
                            normalizeAlarmType(log.alarm_type) === 'battery' ? (
                              <button
                                className="px-2.5 py-1 text-xs font-medium rounded text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-60"
                                onClick={() => dismissBatteryAlarm(log)}
                                disabled={!userDisplayName}
                                title={!userDisplayName ? 'Sign in to dismiss' : 'Dismiss battery alert'}
                              >
                                Dismiss
                              </button>
                            ) : (
                              <button
                                className="px-2.5 py-1 text-xs font-medium rounded text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                                onClick={() => openAckModal(log)}
                                disabled={!userDisplayName}
                                title={!userDisplayName ? 'Sign in to acknowledge' : 'Review and acknowledge'}
                              >
                                Acknowledge
                              </button>
                            )
                          ) : (
                            <span className="text-xs font-medium text-green-600 dark:text-green-400">✓ Acknowledged</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {sortedEvents.length === 0 && !isLoading && (
          <div className="bg-app-panel dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="text-gray-500 dark:text-gray-400 font-medium mb-1">No alarm events found</div>
            <div className="text-gray-400 dark:text-gray-500 text-sm">There are no alarm events to display at this time.</div>
          </div>
        )}
      </div>
      <Dialog isOpen={bulkAckModalOpen} onClose={closeBulkAckModal} panelClassName="max-w-md">
        <div className="pr-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Acknowledge selected alarms</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            A note is required. It will be stored on every log row for the {selectedEventIds.size} selected event
            {selectedEventIds.size !== 1 ? 's' : ''}.
          </p>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Note <span className="text-red-600 dark:text-red-400">*</span>
          </label>
          <textarea
            className="w-full min-h-[100px] p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-y"
            value={bulkAckNote}
            onChange={e => setBulkAckNote(e.target.value)}
            placeholder="Reason or action taken for this bulk acknowledgment…"
            disabled={isBulkAcknowledging}
            required
            aria-required
          />
          {bulkAckError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-3">{bulkAckError}</p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-app-inset dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              onClick={closeBulkAckModal}
              disabled={isBulkAcknowledging}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              onClick={confirmBulkAcknowledge}
              disabled={isBulkAcknowledging || !bulkAckNote.trim()}
            >
              {isBulkAcknowledging ? 'Acknowledging…' : 'Acknowledge all selected'}
            </button>
          </div>
        </div>
      </Dialog>
      <AlarmAcknowledgeDialog
        isOpen={!!ackModalLog}
        onClose={closeAckModal}
        alarmType={String(ackModalLog?.alarm_type ?? '')}
        sensorName={ackModalLog ? getSensorInfo(ackModalLog.sensor_id).name : ''}
        serialDisplay={ackModalSerialDisplay}
        alarmValues={ackModalLog?.alarm_values}
        activatedAt={ackModalLog?.activated}
        alarmResetAt={ackModalLog?.alarm_reset ?? null}
        eventId={ackModalLog?.event_id}
        noteDraft={ackModalNote}
        onNoteChange={setAckModalNote}
        onAcknowledge={confirmAckFromModal}
        isSubmitting={ackModalSubmitting}
        error={ackModalError}
      />
      {editNoteLogId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-md mx-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Note</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Add or modify the note for this alarm event
              </p>
            </div>
            <textarea
              className="w-full h-28 p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-4 bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              value={noteInput}
              onChange={e => setNoteInput(e.target.value)}
              placeholder="Enter note..."
              disabled={isSavingNote}
            />
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-app-inset dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                onClick={() => setEditNoteLogId(null)}
                disabled={isSavingNote}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 disabled:opacity-60"
                onClick={() => handleSaveNote(editNoteLogId)}
                disabled={isSavingNote}
              >
                {isSavingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 